'use client';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'title' | 'card' | 'image' | 'circle' | 'badge';
  delay?: number;
}

export function Skeleton({ className = '', variant = 'text', delay = 0 }: SkeletonProps) {
  const baseClass = 'bg-gradient-to-r from-stone-200 via-stone-100 to-stone-200 rounded animate-shimmer';
  
  const variantClasses = {
    text: 'h-4 w-full',
    title: 'h-8 w-3/4',
    card: 'h-32 w-full',
    image: 'aspect-video w-full',
    circle: 'h-10 w-10 rounded-full',
    badge: 'h-5 w-16 rounded-full',
  };

  const style = delay > 0 ? { animationDelay: `${delay}ms` } : undefined;

  return (
    <div 
      className={`${baseClass} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
}

export function ArticleCardSkeleton({ index = 0 }: { index?: number }) {
  const delay = index * 100;
  
  return (
    <div 
      className="border-b border-stone-300 py-4 opacity-0 animate-fade-in"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
    >
      <Skeleton variant="title" className="mb-3" delay={delay} />
      <Skeleton variant="text" className="w-2/3 mb-2" delay={delay + 50} />
      <div className="flex gap-2 items-center">
        <Skeleton variant="badge" delay={delay + 100} />
        <Skeleton variant="text" className="w-20 h-3" delay={delay + 100} />
      </div>
    </div>
  );
}

export function FeaturedCardSkeleton() {
  return (
    <div className="pb-6 mb-6 border-b border-stone-300 opacity-0 animate-fade-in" style={{ animationFillMode: 'forwards' }}>
      <div className="md:flex md:gap-6">
        <div className="md:w-1/3 mb-4 md:mb-0">
          <Skeleton variant="image" className="rounded-lg" />
        </div>
        <div className="md:w-2/3">
          <Skeleton variant="badge" className="mb-3" delay={50} />
          <Skeleton variant="title" className="mb-4 h-10" delay={100} />
          <Skeleton variant="text" className="mb-2" delay={150} />
          <Skeleton variant="text" className="w-2/3 mb-4" delay={200} />
          <div className="flex gap-2">
            <Skeleton variant="circle" className="h-6 w-6" delay={250} />
            <Skeleton variant="text" className="w-24 h-3" delay={250} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ClusterPageSkeleton() {
  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Skeleton className="h-8 w-32" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Headline skeleton */}
        <div className="mb-8 opacity-0 animate-fade-in" style={{ animationFillMode: 'forwards' }}>
          <Skeleton variant="badge" className="mb-3" />
          <Skeleton variant="title" className="h-12 mb-4" delay={100} />
          <Skeleton variant="text" className="h-5 mb-2" delay={150} />
          <Skeleton variant="text" className="w-3/4" delay={200} />
        </div>

        {/* Facts skeleton */}
        <div 
          className="mb-8 p-4 bg-green-50/50 border-l-4 border-green-200 rounded-r opacity-0 animate-slide-in"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <Skeleton className="h-4 w-1/4 mb-3 bg-green-100" />
          <Skeleton className="h-3 w-full mb-2 bg-green-100" />
          <Skeleton className="h-3 w-5/6 mb-2 bg-green-100" />
          <Skeleton className="h-3 w-4/6 bg-green-100" />
        </div>

        <div className="border-t-2 border-stone-300 my-8" />

        {/* Narratives skeleton */}
        <Skeleton className="h-6 w-1/3 mb-6" delay={300} />
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div 
              key={i} 
              className="p-4 border-l-4 border-stone-200 bg-stone-50 rounded-r opacity-0 animate-slide-in"
              style={{ animationDelay: `${400 + i * 100}ms`, animationFillMode: 'forwards' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Skeleton variant="circle" className="h-6 w-6" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-3 w-full mb-1" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function HomePageSkeleton() {
  return (
    <div className="space-y-4">
      <FeaturedCardSkeleton />
      <div className="grid md:grid-cols-3 gap-8 mt-8">
        <div className="md:col-span-2 space-y-4">
          {[0, 1, 2, 3].map((i) => (
            <ArticleCardSkeleton key={i} index={i} />
          ))}
        </div>
        <aside 
          className="opacity-0 animate-slide-in"
          style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
        >
          <Skeleton className="h-5 w-24 mb-4" />
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-2 items-start">
                <Skeleton variant="badge" className="flex-shrink-0" delay={400 + i * 50} />
                <Skeleton variant="text" className="h-4" delay={400 + i * 50} />
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

// New: Inline loading spinner
export function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-3',
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} border-stone-300 border-t-stone-800 rounded-full animate-spin`}
    />
  );
}

// New: Refreshing indicator
export function RefreshingBar() {
  return (
    <div className="fixed top-0 left-0 right-0 h-1 bg-stone-100 overflow-hidden z-50">
      <div 
        className="h-full bg-gradient-to-r from-red-500 via-stone-500 to-blue-500 animate-shimmer"
        style={{ width: '50%' }}
      />
    </div>
  );
}
